import {Inject, Injectable} from '@angular/core';
import {FirebaseApp} from '@angular/fire/app';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {getFunctions, httpsCallable} from '@angular/fire/functions';
import {firstValueFrom, Observable, of, Subject} from 'rxjs';
import {first, map, switchMap, take, takeUntil} from 'rxjs/operators';
import {environment} from 'src/environments/environment';
import {User} from '../models/User';
import {SnackbarService} from './snackbar.service';
import firebase from 'firebase/compat/app';
import {Timestamp, deleteField} from 'firebase/firestore';


@Injectable({
  providedIn: 'root',
})
export class UserService {
  private ngUnsubscribe = new Subject();
  private setUserSuperAdminFn: (data: {userId: string; superAdmin: boolean}) => Promise<unknown>;

  constructor(
    public afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private snackbarservice: SnackbarService,
    @Inject(FirebaseApp) private app: FirebaseApp,
  ) {
    this.setUserSuperAdminFn = httpsCallable(getFunctions(this.app, 'europe-west1'), 'setUserSuperAdmin');
  }

  isLoggedIn() {
    return this.afAuth.authState.pipe(first());
  }

  getUsers() {
    return this.afs.collection('users').valueChanges();
  }

  getCurrentUser() {
    return this.afAuth.authState.pipe(
      take(1),
      switchMap((firebaseUser) => {
        // const user: User = {
        //   firstname: firebaseUser.displayName,
        //   email: firebaseUser.email,
        // }
        return of(firebaseUser);
      })
    );
  }

  async getFirebaseUser(): Promise<firebase.User | null> {
    return await firstValueFrom(this.afAuth.authState.pipe(
      take(1))).then(firebaseUser => {
      return firebaseUser;
    });
  }


  async getUserByFirebaseUserId(firebaseUserData: firebase.User): Promise<User | undefined> {
    const queryByFirebaseId$ = this.afs.collection('users', ref => ref.where('firebaseUser.uid', '==', firebaseUserData.uid)).get();
    const queryByEmail$ = this.afs.collection('users', ref => ref.where('email', '==', firebaseUserData.email)).get();

    const [resByFirebaseId, resByEmail] = await Promise.all([
      firstValueFrom(queryByFirebaseId$),
      firstValueFrom(queryByEmail$)
    ]);

    const users: User[] = [];
    resByFirebaseId.docs.forEach(doc => {
      const user = doc.data() as User;
      user.id = doc.id;
      if (!users.some(u => u.id === user.id)) {
        users.push(user);
      }
    });
    resByEmail.docs.forEach(doc => {
      const user = doc.data() as User;
      user.id = doc.id;
      if (!users.some(u => u.id === user.id)) {
        users.push(user);
      }
    });

    return users.length === 0 ? undefined : users[0];
  }

  getUserWithFirebaseUserData(firebaseUserData: firebase.User): Observable<User | undefined> {
    return this.afs
      .collection('users').snapshotChanges()
      .pipe(
        map((actions) => {
          const filledUsers = actions.map((action) => {
            const id = action.payload.doc.id;
            const user = {...action.payload.doc.data() as User};
            const fromCache = (action.payload.doc as firebase.firestore.DocumentSnapshot).metadata.fromCache;
            return {id, fromCache, ...user} as User;
          });
          return filledUsers.find(user =>
            user.firebaseUser?.uid === firebaseUserData.uid ||
            user.id === firebaseUserData.uid ||
            user.email === firebaseUserData.email
          );
        }));
  }

  getUser(userId?: string): Observable<User | undefined> {
    return this.afAuth.authState.pipe(
      takeUntil(this.ngUnsubscribe),
      switchMap((firebaseUser) => {
        if (firebaseUser) {
          const id = userId && userId.length > 0 ? userId : firebaseUser.uid;
          const userDoc = this.afs.doc<User>(`users/${id}`);
          return userDoc.valueChanges().pipe(
            switchMap((user) => {
              if (user) {
                return of(user);
              } else {
                const userByFirebaseUid$ = this.getUserByFirebaseUid(firebaseUser.uid);
                const userByEmail$ = this.getUserByEmail(firebaseUser.email || '');
                return userByFirebaseUid$.pipe(
                  switchMap((userByUid) => {
                    if (userByUid) {
                      console.log(`User found by firebase uid ${firebaseUser.uid}`);
                      return of(userByUid);
                    } else {
                      return userByEmail$.pipe(
                        map((userByEmail) => {
                          if (userByEmail) {
                            console.log(`User found by email ${firebaseUser.email}`);
                            return userByEmail;
                          } else {
                            console.log(`User not found by firebase uid ${firebaseUser.uid} or email ${firebaseUser.email}`);
                            return undefined;
                          }
                        })
                      );
                    }
                  })
                );
              }
            })
          );
        }
        return of(undefined);
      })
    );
  }

  private getUserByFirebaseUid(uid: string): Observable<User | undefined> {
    if (!uid || uid.length === 0) {
      return of(undefined);
    }
    return this.afs.collection<User>('users', ref => ref.where('firebaseUser.uid', '==', uid))
      .valueChanges()
      .pipe(
        map(users => users.length > 0 ? users[0] : undefined)
      );
  }

  private getUserByEmail(email: string): Observable<User | undefined> {
    if (!email || email.length === 0) {
      return of(undefined);
    }
    return this.afs.collection<User>('users', ref => ref.where('email', '==', email))
      .valueChanges()
      .pipe(
        map(users => users.length > 0 ? users[0] : undefined)
      );
  }

  isAdmin() {
    return this.getUser().subscribe((u) => {
      return u?.role?.name === 'admin';
    });
  }

  isSuperAdmin() {
    return this.getUser().subscribe((u) => {
      return u?.superAdmin;
    });
  }

  async updateUserAppversion(u: User) {
    const appVersion = environment.appVersion;
    await this.afs.doc('users/' + u.id).update({
      appVersion,
      lastupdated: Timestamp.now(),
    });
  }

  /**
   * Adds a project access entry to the user's projectaccess array in a robust, observable-friendly way.
   * Always creates a new array reference and updates Firestore.
   */
  async addProjectAccess(u: User, newProjectAccess: any) {
    const current = Array.isArray(u.projectaccess) ? u.projectaccess : [];
    // Prevent duplicates (optional, based on your logic)
    if (!current.some(pa => JSON.stringify(pa) === JSON.stringify(newProjectAccess))) {
      u.projectaccess = [...current, newProjectAccess];
      await this.updateUserProjectAccess(u);
    }
  }

  /**
   * Removes a project access entry from the user's projectaccess array in a robust, observable-friendly way.
   * Always creates a new array reference and updates Firestore.
   */
  async removeProjectAccess(u: User, predicate: (pa: any) => boolean) {
    const current = Array.isArray(u.projectaccess) ? u.projectaccess : [];
    u.projectaccess = current.filter(pa => !predicate(pa));
    await this.updateUserProjectAccess(u);
  }

  /**
   * Updates the user's projectaccess array in Firestore.
   * Always updates with a new array reference (or deletes field if empty).
   * Ensures lastupdated is set and logs errors.
   */
  async updateUserProjectAccess(u: User) {
    try {
      await this.afs.doc('users/' + u.id).update({
        projectaccess: (Array.isArray(u.projectaccess) && u.projectaccess.length > 0)
          ? [...u.projectaccess]
          : deleteField(),
        lastupdated: Timestamp.now(),
      });
    } catch (e) {
      console.error('Firestore update error:', e);
    }
  }

  /**
   * Replaces the user's projectaccess array with a new array and updates Firestore.
   * Ensures a new array reference and robust observable emission.
   */
  async setProjectAccess(u: User, newProjectAccess: any[]) {
    u.projectaccess = Array.isArray(newProjectAccess) ? [...newProjectAccess] : [];
    await this.updateUserProjectAccess(u);
  }

  async updateUserLastseen(u: User) {
    await this.afs.doc('users/' + u.id).update({
      lastseen: Timestamp.now(),
    });
  }

  async updatePositionConsent(u: User) {
    await this.afs.doc('users/' + u.id).update({
      positionconsent: {
        approved: true,
        lastupdated: Timestamp.now(),
      },
    });
  }

  async updateUser(user: User): Promise<void> {
    await this.afs.doc('users/' + user.id).update(user);
  }

  async setUserSuperAdmin(userId: string, superAdmin: boolean): Promise<void> {
    await this.setUserSuperAdminFn({userId, superAdmin});
  }

  sendPasswordResetMail(email: string) {
    this.afAuth.sendPasswordResetEmail(email).then(() => {
      this.snackbarservice.openSnackBar('Mail om passord reset sendt', '');
    });
  }

  async createUser(user: User): Promise<string> {
    const docRef = await this.afs
      .collection('newusers')
      .add(user);
    console.log('Adding user', user);
    this.snackbarservice.openSnackBar(
      user.firstname + ' ' + user.lastname + ' opprettes...',
      ''
    );
    return docRef.id;
  }
}
